/**
 * Zoom policy: the PAGE never zooms, by any gesture; the ENLARGED CARD
 * magnifies in-app under a pinch, and springs back the moment a finger lifts.
 *
 * Two product decisions, in the owner's words:
 *   2026-07-29  "i wanted zoom blocked everywhere regardless of triggering
 *               gesture". The browser's own zoom broke screens: on iOS a
 *               pinch fires window `resize` with the visual viewport shrunk
 *               and layout code re-laid the screen out around it.
 *   2026-09-19  "on mobile, allow zooming on cards, just for purpose of
 *               enlarging text/assets, especially without reordering elements
 *               when it happens in any way", refined on 2026-09-20 after a
 *               day of NATIVE pinch gated to cards on his iPhone: lifting the
 *               fingers must zoom back out (native zoom "froze" magnified),
 *               un-enlarged cards must not zoom, nothing may move while it
 *               happens, and zooming in and back had left a black band under
 *               the screen that only a rotation cleared.
 *
 * So the browser's zoom stays off everywhere (part 1) and the magnifier is
 * ours (part 2). A pinch with a finger on the ENLARGED card — the popup that
 * tap-to-inspect opens: `#card-hover-preview` on every catalog, in the battle
 * and in Moon Fuse, `#mulligan-card-hover-preview` in the mulligan — scales
 * that popup with a transform about the point between the fingers. A
 * transform touches no layout, so the rest of the screen is not consulted,
 * let alone re-laid out; the viewport never changes, so no `resize` fires
 * and nothing re-paginates, re-seats or dismisses. While the fingers are down
 * they own the screen: no pointer move reaches the page, so no hover engine,
 * teardown watchdog, tilt or control-seating loop answers them. When a finger
 * lifts the popup eases back to its resting size. A rest-size card — in the
 * hand, in a grid, in a deck lane — is not magnifiable: a pinch there, or on
 * bare board, is cancelled and nothing happens.
 *
 * PART 1 — NO NATIVE ZOOM.
 * `touch-action: pan-x pan-y` in `styles/touch-policy.css` removes double-tap
 * zoom and pinch zoom wherever touch-action is honoured. Safari is the gap: it
 * treats pinch as a SYSTEM gesture and raises its own non-standard
 * `gesturestart` / `gesturechange` / `gestureend` events, which no touch-action
 * value suppresses, and it has ignored `user-scalable=no` since iOS 10. Those
 * events are cancelable, so cancelling them is the only thing that actually
 * holds on an iPhone — and every iPhone browser is WebKit, so this is not an
 * edge case, it is the majority of the phone traffic this game gets. The
 * two-finger `touchmove` every engine delivers is cancelled as well: that is
 * what keeps Chrome from starting its pinch, and it is also what hands the
 * fingers to part 2 instead of to the browser.
 *
 * DOUBLE-TAP IS NOT HANDLED HERE, DELIBERATELY. Any `touch-action` value other
 * than `auto` already disables double-tap zoom in every engine including
 * WebKit, so the root policy covers it. A `touchend` guard would be redundant
 * AND dangerous: cancelling a touchend suppresses the compatibility click, and
 * this game's core phone gesture is "tap to inspect, tap again to act". The
 * cheap-looking guard would break the product's own second tap.
 *
 * Loaded FIRST by every document the player can reach: a policy that ships on
 * one page is not a policy, and the magnifier's capture listeners must be
 * registered before every module that listens on the document — listeners on
 * one target fire in registration order, and a pointer move must be withheld
 * here before a hover engine can hear it. Both are asserted by
 * tests/unit/styles/battleZoomPolicy.test.js.
 */
(function () {
  'use strict';

  /* A second evaluation of this file (a duplicated script tag, a hot reload, a
     test re-requiring it) would leave the previous instance listening on the
     document — two magnifiers answering one pinch. Retire the old one first. */
  try {
    if (window.MoonfallZoomPolicy && typeof window.MoonfallZoomPolicy.destroy === 'function') {
      window.MoonfallZoomPolicy.destroy();
    }
  } catch (_) { /* nothing to retire */ }

  var subscriptions = [];
  function listen(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    subscriptions.push([target, type, handler, options]);
  }

  // --- 1. No native zoom, by any gesture --------------------------------------

  // Safari's own pinch. Passive listeners cannot cancel, so these are explicit.
  ['gesturestart', 'gesturechange', 'gestureend'].forEach(function (type) {
    listen(document, type, function (event) {
      if (event.cancelable) event.preventDefault();
    }, { passive: false });
  });

  // --- 2. The magnifier -------------------------------------------------------

  /* The enlarged card, and nothing else: the popup tap-to-inspect opens. Every
     catalog, the battle and Moon Fuse build `#card-hover-preview`; the
     mulligan moves its card into `#mulligan-card-hover-preview`. Rest-size
     cards are deliberately absent — "zooming on unenlarged cards should not
     be possible". */
  var MAGNIFIER_SURFACES = '#card-hover-preview, #mulligan-card-hover-preview';
  var MAX_SCALE = 4;
  /* Spread before the popup answers. A two-finger tap, a rest, or two fingers
     closing (there is nothing to shrink) never moves it. */
  var ACTIVATION_PX = 6;
  /* The spring back to rest once a finger lifts — "lifting hands should auto
     zoom out". Short enough to read as release, long enough not to snap. */
  var RELEASE_MS = 180;
  var RELEASE_EASING = 'cubic-bezier(0.2, 0.7, 0.2, 1)';
  /* "On mobile": the stylesheet's own media block, so the magnifier answers
     exactly where the CSS policy applies. */
  var POLICY_MEDIA = '(any-pointer: coarse), (max-width: 1024px)';
  var MAGNIFYING_CLASS = 'is-magnifying';

  var session = null;      // the pinch under way, see startSession
  var settlingHost = null; // a popup easing back after release
  var settleTimer = null;
  var settleUntil = 0;

  function policyApplies() {
    try {
      return typeof window.matchMedia !== 'function' || window.matchMedia(POLICY_MEDIA).matches;
    } catch (_) {
      return true;
    }
  }

  function point(touch) {
    return { x: Number(touch.clientX) || 0, y: Number(touch.clientY) || 0 };
  }

  function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function midpoint(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  function rectContains(rect, p) {
    return !!rect && rect.width > 0 && rect.height > 0
      && p.x >= rect.left && p.x <= rect.right && p.y >= rect.top && p.y <= rect.bottom;
  }

  function isShowing(host) {
    try {
      var style = window.getComputedStyle(host);
      return style.display !== 'none' && style.visibility !== 'hidden';
    } catch (_) {
      return true;
    }
  }

  /**
   * The enlarged card one of the two fingers landed on, or null. Decided by
   * GEOMETRY, not by hit target: the battle popup is pointer-transparent on
   * purpose (a tap through it lands on the very card it enlarged), so a finger
   * on it hit-tests to the board slot underneath. One finger, not two — the
   * fingers of a natural pinch start close together, and demanding both inside
   * the card would refuse a pinch that began at its edge.
   */
  function magnifiableHost(a, b) {
    var hosts = document.querySelectorAll(MAGNIFIER_SURFACES);
    for (var i = 0; i < hosts.length; i += 1) {
      var host = hosts[i];
      if (!isShowing(host)) continue;
      var card = host.querySelector('.card');
      var boxes = [host.getBoundingClientRect(), card ? card.getBoundingClientRect() : null];
      for (var j = 0; j < boxes.length; j += 1) {
        if (rectContains(boxes[j], a) || rectContains(boxes[j], b)) return host;
      }
    }
    return null;
  }

  function clearMagnification(host) {
    if (!host) return;
    host.classList.remove(MAGNIFYING_CLASS);
    host.style.removeProperty('transform');
    host.style.removeProperty('transform-origin');
    host.style.removeProperty('transition');
  }

  function stopSettling() {
    if (settleTimer !== null) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }
    if (settlingHost) clearMagnification(settlingHost);
    settlingHost = null;
    settleUntil = 0;
  }

  function startSession(host, touchA, touchB) {
    /* A popup still easing back from the previous pinch returns to rest first,
       so it is measured at rest: the transform below is built on that box. */
    stopSettling();
    clearMagnification(host);
    var card = host.querySelector('.card');
    var box = card ? card.getBoundingClientRect() : null;
    if (!box || !box.width || !box.height) box = host.getBoundingClientRect();
    var hostBox = host.getBoundingClientRect();
    var centre = { x: box.left + box.width / 2, y: box.top + box.height / 2 };
    var a = point(touchA);
    var b = point(touchB);
    var byId = typeof touchA.identifier === 'number' && typeof touchB.identifier === 'number';
    session = {
      host: host,
      byId: byId,
      ids: byId ? [touchA.identifier, touchB.identifier] : [0, 1],
      /* The card's centre, and the same point as the host's transform origin,
         so the maths below is about the CARD whatever padding the host has. */
      centre: centre,
      originX: centre.x - hostBox.left,
      originY: centre.y - hostBox.top,
      startDistance: distance(a, b),
      startMidpoint: midpoint(a, b),
      active: false,
      last: '',
    };
    host.classList.add(MAGNIFYING_CLASS);
  }

  /** The session's two fingers as they are now, or null once either has lifted. */
  function sessionTouches(touches) {
    if (!session || !touches || touches.length < 2) return null;
    if (!session.byId) return [touches[0], touches[1]];
    var found = [null, null];
    for (var i = 0; i < touches.length; i += 1) {
      if (touches[i].identifier === session.ids[0]) found[0] = touches[i];
      else if (touches[i].identifier === session.ids[1]) found[1] = touches[i];
    }
    return found[0] && found[1] ? found : null;
  }

  function applyMagnification(scale, tx, ty) {
    var host = session.host;
    var value = 'translate(' + tx.toFixed(1) + 'px, ' + ty.toFixed(1) + 'px) scale(' + scale.toFixed(3) + ')';
    if (value === session.last) return;
    session.last = value;
    host.style.transformOrigin = session.originX.toFixed(1) + 'px ' + session.originY.toFixed(1) + 'px';
    host.style.transition = 'none';
    host.style.transform = value;
  }

  function onMagnifierMove(event) {
    var s = session;
    if (!s) return;
    if (!s.host.isConnected) {
      endSession(true);
      return;
    }
    /* A move the browser will not let us cancel is a scroll already under
       way: the fingers are the browser's, not the magnifier's. */
    if (!event.cancelable) {
      endSession(true);
      return;
    }
    var pair = sessionTouches(event.touches);
    if (!pair) {
      endSession(false);
      return;
    }
    var a = point(pair[0]);
    var b = point(pair[1]);
    var spread = distance(a, b);
    if (!s.active) {
      if (spread - s.startDistance < ACTIVATION_PX) return;
      /* Activation re-bases the gesture so the scale ramps from exactly 1
         here instead of jumping by the activation spread. */
      s.active = true;
      s.startDistance = spread;
      s.startMidpoint = midpoint(a, b);
      return;
    }
    var scale = s.startDistance > 0 ? spread / s.startDistance : 1;
    scale = Math.min(MAX_SCALE, Math.max(1, scale));
    /* The point of the card that was between the fingers stays between the
       fingers: with the origin at the card's centre C, a rest point P lands at
       C + scale·(P − C) + T, and P = the starting midpoint must land at the
       current midpoint. */
    var m = midpoint(a, b);
    var tx = m.x - s.centre.x - scale * (s.startMidpoint.x - s.centre.x);
    var ty = m.y - s.centre.y - scale * (s.startMidpoint.y - s.centre.y);
    applyMagnification(scale, tx, ty);
  }

  function endSession(immediate) {
    var s = session;
    session = null;
    if (!s) return;
    var host = s.host;
    if (immediate || !s.active || !host.isConnected) {
      clearMagnification(host);
      return;
    }
    settlingHost = host;
    settleUntil = Date.now() + RELEASE_MS;
    host.style.transition = 'transform ' + RELEASE_MS + 'ms ' + RELEASE_EASING;
    host.style.transform = 'translate(0px, 0px) scale(1)';
    settleTimer = setTimeout(function () {
      settleTimer = null;
      settlingHost = null;
      settleUntil = 0;
      clearMagnification(host);
    }, RELEASE_MS + 40);
  }

  /** True while a pinch holds the enlarged card, and while it eases back. */
  function isMagnifying() {
    return !!session || Date.now() < settleUntil;
  }

  listen(document, 'touchstart', function (event) {
    var touches = event.touches;
    if (session || !touches || touches.length < 2 || !policyApplies()) return;
    var host = magnifiableHost(point(touches[0]), point(touches[1]));
    if (host) startSession(host, touches[0], touches[1]);
  }, { capture: true, passive: true });

  /* Two contacts: never the browser's pinch (part 1), and the magnifier's
     input while a session holds the enlarged card (part 2). Single-touch
     scrolling is untouched — the length check is what keeps every list
     scrollable. Capture, so this runs before any other touchmove listener. */
  listen(document, 'touchmove', function (event) {
    var touches = event.touches;
    if (!touches || touches.length < 2) return;
    if (event.cancelable) event.preventDefault();
    onMagnifierMove(event);
  }, { capture: true, passive: false });

  function onTouchSettle(event) {
    if (!session) return;
    // A third finger lifting leaves the pinch intact.
    if (sessionTouches(event.touches)) return;
    endSession(false);
  }
  listen(document, 'touchend', onTouchSettle, { capture: true, passive: true });
  listen(document, 'touchcancel', onTouchSettle, { capture: true, passive: true });

  /* While a pinch holds the card, its fingers are not pointers the page may
     follow. Every document-level pointermove listener — the mulligan's
     leave-the-popup teardown, the pack popup's watchdog, the hover engines,
     the tilt that follows one finger — would otherwise answer the two fingers
     and move, tear down or re-light the very card being read. Stopped at
     capture on the document, ahead of all of them (this file loads first).
     pointerup and pointercancel pass: the tap-latch and the drag system must
     see every finger lift. */
  listen(document, 'pointermove', function (event) {
    if (session && event.pointerType === 'touch') event.stopImmediatePropagation();
  }, { capture: true });

  function destroy() {
    endSession(true);
    stopSettling();
    subscriptions.forEach(function (entry) {
      try {
        entry[0].removeEventListener(entry[1], entry[2], entry[3]);
      } catch (_) { /* a target that cannot unsubscribe must not keep the rest alive */ }
    });
    subscriptions = [];
  }

  /* Read by touch-preview.js (its declared-action control keeps its seat while
     the card it sits beside is magnified) and by the tests. Frozen so a page
     cannot widen the magnifier at runtime. */
  window.MoonfallZoomPolicy = Object.freeze({
    MAGNIFIER_SURFACES: MAGNIFIER_SURFACES,
    MAX_SCALE: MAX_SCALE,
    RELEASE_MS: RELEASE_MS,
    isMagnifying: isMagnifying,
    destroy: destroy,
  });
}());
